import { useState, type ReactNode } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Archive,
  ArchiveRestore,
  Pencil,
  Plus,
  Trash2,
  ArrowLeft,
  MoreHorizontal,
  X,
  FolderOpen,
  Building2,
  User,
} from "lucide-react";
import {
  useProjectGroup,
  useUpdateProjectGroup,
  useSoftDeleteProjectGroup,
  type ProjectGroupUpdate,
} from "@/hooks/useProjectGroups";
import {
  ProjectWithRelations,
  useProjects,
  useProjectWriteAvailability,
  useUpdateProject,
} from "@/hooks/useProjects";
import { ProjectGroupForm } from "@/components/forms/ProjectGroupForm";
import { CreateProjectDialog } from "@/components/calendar/CreateProjectDialog";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { format } from "date-fns";
import { useSetCurrentEntity } from "@/contexts/RightSidebarContext";
import { usePageHeader } from "@/hooks/usePageHeader";
import { QueryResult } from "@/components/QueryResult";
import { formatProjectName } from "@/lib/format-project-name";
import { ProjectGroupMeetingNotesSection } from "@/components/project-group-detail/ProjectGroupMeetingNotesSection";
import { TimeTrackingCard } from "@/components/project-detail/finance/TimeTrackingCard";
import { cn } from "@/lib/utils";

const COLOR_CLASSES: Record<string, string> = {
  blue: "bg-blue-500",
  green: "bg-green-500",
  yellow: "bg-yellow-500",
  red: "bg-red-500",
  purple: "bg-purple-500",
  orange: "bg-orange-500",
  pink: "bg-pink-500",
};

type GroupProject = Pick<
  ProjectWithRelations,
  | "id"
  | "name"
  | "number"
  | "status"
  | "event_start_date"
  | "event_end_date"
  | "deleted_at"
>;

function ProjectLinkCell({
  children,
  className,
  to,
}: {
  children: ReactNode;
  className?: string;
  to: string;
}) {
  return (
    <TableCell className={cn("p-0", className)}>
      <Link to={to} className="block h-full p-4 text-inherit no-underline">
        {children}
      </Link>
    </TableCell>
  );
}

export default function ProjectGroupDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isAddProjectOpen, setIsAddProjectOpen] = useState(false);
  const [isNewProjectOpen, setIsNewProjectOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );

  const { data: group, isLoading, isError, error } = useProjectGroup(id);
  const { data: allProjects } = useProjects();
  useSetCurrentEntity("project_group", id, group?.name);
  const updateGroup = useUpdateProjectGroup();
  const softDeleteGroup = useSoftDeleteProjectGroup();
  const updateProject = useUpdateProject();
  const { canWrite, reason: writeUnavailableReason } =
    useProjectWriteAvailability();

  const isArchived = group?.status === "archived";

  // Projects not already in this group
  const availableProjects =
    allProjects?.filter((p) => !p.deleted_at && p.project_group_id !== id) ||
    [];

  const groupProjects =
    allProjects?.filter((p) => !p.deleted_at && p.project_group_id === id) ??
    ((group?.projects as GroupProject[] | undefined)?.filter(
      (p) => !p.deleted_at,
    ) ||
      []);

  usePageHeader({
    title: group?.name,
  });

  const handleUpdate = async (data: ProjectGroupUpdate) => {
    if (!id) return;
    try {
      await updateGroup.mutateAsync({ id, updates: data });
      setIsEditOpen(false);
    } catch (error) {
      // Error handled in hook
    }
  };

  const handleToggleArchive = async () => {
    if (!id || !group) return;
    try {
      await updateGroup.mutateAsync({
        id,
        updates: { status: group.status === "active" ? "archived" : "active" },
      });
    } catch (error) {
      // Error handled in hook
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await softDeleteGroup.mutateAsync(id);
      navigate("/projects/groups");
    } catch (error) {
      // Error handled in hook
    }
  };

  const handleAddProject = async () => {
    if (!selectedProjectId || !id) return;
    try {
      await updateProject.mutateAsync({
        id: selectedProjectId,
        updates: { project_group_id: id },
      });
      await queryClient.invalidateQueries({ queryKey: ["project_groups"] });
      setSelectedProjectId(null);
      setIsAddProjectOpen(false);
    } catch (error) {
      // Error handled in hook
    }
  };

  const handleRemoveProject = async (projectId: string) => {
    try {
      await updateProject.mutateAsync({
        id: projectId,
        updates: { project_group_id: null },
      });
      await queryClient.invalidateQueries({ queryKey: ["project_groups"] });
    } catch (error) {
      // Error handled in hook
    }
  };

  const handleProjectCreated = (projectId: string) => {
    setIsNewProjectOpen(false);
    navigate(`/projects/${projectId}/settings`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!group) {
    return (
      <QueryResult
        isError={isError}
        error={error}
        data={group}
        entityName="project group"
        backTo="/projects/groups"
        backLabel="Back to Groups"
      >
        {null}
      </QueryResult>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/projects/groups")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold">{group.name}</h1>
        <div className="flex items-center gap-2 ml-auto">
          {!isArchived && (
            <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Edit Project Group</DialogTitle>
                </DialogHeader>
                <ProjectGroupForm
                  defaultValues={{
                    name: group.name,
                    description: group.description || "",
                    customer_id: group.customer_id,
                    contact_id: group.contact_id,
                    status: group.status as "active" | "archived",
                    color: group.color,
                  }}
                  onSubmit={handleUpdate}
                  isLoading={updateGroup.isPending}
                />
              </DialogContent>
            </Dialog>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" aria-label="Project group actions">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleToggleArchive}>
                {isArchived ? (
                  <>
                    <ArchiveRestore className="h-4 w-4 mr-2" />
                    Unarchive
                  </>
                ) : (
                  <>
                    <Archive className="h-4 w-4 mr-2" />
                    Archive
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive"
                offlineEdit
                onClick={handleDelete}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Badges */}
      <div className="flex items-center gap-3 flex-wrap">
        {group.color && (
          <div
            className={`w-4 h-4 rounded-full ${COLOR_CLASSES[group.color] || "bg-gray-500"}`}
          />
        )}
        {group.number && (
          <span className="text-muted-foreground font-mono text-sm">
            ({group.number})
          </span>
        )}
        <Badge variant={group.status === "active" ? "default" : "secondary"}>
          {group.status}
        </Badge>
      </div>

      {/* Group Details */}
      <Card>
        <CardHeader>
          <CardTitle>Group Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {group.companies && (
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Customer:</span>
                <span className="text-sm">{group.companies.name}</span>
              </div>
            )}
            {group.contact && (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Contact:</span>
                <span className="text-sm">
                  {group.contact.first_name} {group.contact.last_name}
                </span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <FolderOpen className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Projects:</span>
              <span className="text-sm">{groupProjects.length}</span>
            </div>
          </div>
          {group.description && (
            <div>
              <span className="text-sm text-muted-foreground">
                Description:
              </span>
              <p className="text-sm mt-1">{group.description}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Overview */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Overview</h3>
        <TimeTrackingCard
          projectGroupId={group.id}
          emptyLabel="No time entries synced for this project group"
        />
      </div>

      {/* Projects */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            Projects ({groupProjects.length})
          </h3>
          <div className="flex gap-2">
            {!isArchived && (
              <>
                <Dialog
                  open={isAddProjectOpen}
                  onOpenChange={setIsAddProjectOpen}
                >
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      disabled={!canWrite}
                      title={writeUnavailableReason ?? undefined}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Existing Project
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Project to Group</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <EntityCombobox
                        options={availableProjects.map((p) => ({
                          value: p.id,
                          label: `${p.number ? `${p.number} - ` : ""}${formatProjectName(
                            p.name,
                            p.event_start_date,
                          )}`,
                        }))}
                        value={selectedProjectId}
                        onChange={setSelectedProjectId}
                        placeholder="Select a project..."
                        disabled={!canWrite}
                      />
                      <Button
                        onClick={handleAddProject}
                        disabled={
                          !canWrite ||
                          !selectedProjectId ||
                          updateProject.isPending
                        }
                        title={writeUnavailableReason ?? undefined}
                      >
                        Add to Group
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <Button
                  onClick={() => setIsNewProjectOpen(true)}
                  disabled={!canWrite}
                  title={writeUnavailableReason ?? undefined}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Project
                </Button>
                <CreateProjectDialog
                  open={isNewProjectOpen}
                  onOpenChange={setIsNewProjectOpen}
                  onSuccess={handleProjectCreated}
                />
              </>
            )}
          </div>
        </div>

        {groupProjects.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                No projects in this group yet.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Event Start</TableHead>
                  <TableHead>Event End</TableHead>
                  {!isArchived && <TableHead className="w-[50px]"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupProjects.map((project) => (
                  <TableRow
                    key={project.id}
                    className="cursor-pointer hover:bg-accent/50"
                  >
                    <ProjectLinkCell
                      to={`/projects/${project.id}`}
                      className="font-mono text-sm"
                    >
                      {project.number || "-"}
                    </ProjectLinkCell>
                    <ProjectLinkCell to={`/projects/${project.id}`}>
                      {formatProjectName(
                        project.name,
                        project.event_start_date,
                      )}
                    </ProjectLinkCell>
                    <ProjectLinkCell to={`/projects/${project.id}`}>
                      <Badge variant="outline">
                        {project.status || "unknown"}
                      </Badge>
                    </ProjectLinkCell>
                    <ProjectLinkCell to={`/projects/${project.id}`}>
                      {project.event_start_date
                        ? format(new Date(project.event_start_date), "PP")
                        : "-"}
                    </ProjectLinkCell>
                    <ProjectLinkCell to={`/projects/${project.id}`}>
                      {project.event_end_date
                        ? format(new Date(project.event_end_date), "PP")
                        : "-"}
                    </ProjectLinkCell>
                    {!isArchived && (
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Remove ${project.name} from group`}
                          disabled={!canWrite}
                          title={writeUnavailableReason ?? undefined}
                          onClick={() => handleRemoveProject(project.id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      {/* Meeting Notes */}
      {id && <ProjectGroupMeetingNotesSection projectGroupId={id} />}
    </div>
  );
}
